import { useEffect, useState } from 'react';
import { useUserStore } from '../../store/useUserStore';
import { FeatureCode } from '../../common/enums/common.enum';
import { loadAdminKindergartens, loadKindergartenFeatures } from './financeDirectory';

/**
 * Whether the sidebar offers Finance at all.
 *
 * Invoicing is switched on per kindergarten, but the sidebar belongs to no kindergarten, so
 * the question it can answer is "does this admin have any kindergarten that invoices". If
 * none does, every Finance page would only say so; hiding the entry says it once. A
 * kindergarten with it switched off is still explained on the page itself.
 *
 * A super admin always sees it: the platform catalogue lives there and belongs to no
 * kindergarten - and a super admin has no admin row, so their kindergarten list is empty.
 *
 * Hidden until known, so it appears once rather than appearing and then vanishing.
 */
export const useFinanceNavVisible = (): boolean => {
  const userInfo = useUserStore(state => state.userInfo);
  const [visible, setVisible] = useState(false);

  const userId = userInfo?.user?.auth0Id;
  const isSuperAdmin = !!userInfo?.isSuperAdmin;

  useEffect(() => {
    // the requests need a signed-in user; before that they would only fail
    if (!userId) return undefined;
    if (isSuperAdmin) {
      setVisible(true);
      return undefined;
    }

    let cancelled = false;
    loadAdminKindergartens()
      .then(kindergartens =>
        Promise.all(
          kindergartens.map(kindergarten =>
            loadKindergartenFeatures(kindergarten.id)
              .then(features => features.some(feature => feature.code === FeatureCode.INVOICING && feature.enabled))
              // one kindergarten failing to answer must not hide Finance for the rest
              .catch(() => false)
          )
        )
      )
      .then(flags => !cancelled && setVisible(flags.some(Boolean)))
      .catch(() => !cancelled && setVisible(false));

    return () => {
      cancelled = true;
    };
  }, [userId, isSuperAdmin]);

  return visible;
};
